import { flatten, isEmpty } from "lodash";
import { createSelector } from "reselect";

import { ValueSource } from "constants/ruleBooks";
import { RuleCustomisableFields } from "constants/rules";

import { selectPrerequisiteRuleChainByRuleIdLookup } from "containers/Settings/RuleBooks/Schema/livestockSaleSchema";

import { getMasterRuleStatus, getGSTStatusFromRule } from "lib/rules";

import { selectRuleIdsByRuleBookIdLookup } from "../indexes";
import { selectMappedLedgerAccountByCodeLookup } from "../ledgerAccounts";
import {
  getBillingTags,
  getMasterRuleBooks,
  getMasterRules,
  getMasterRuleSets,
  getRules,
  getRuleSets,
} from "../root";

export const selectParsedRuleContentByRuleIdLookup = createSelector(
  [getRules],
  ruleLookup =>
    Object.values(ruleLookup).reduce((acc, { id, content }) => {
      acc[id] = JSON.parse(content) || {};
      return acc;
    }, {}),
);

export const selectCustomisationsByRuleIdLookup = createSelector(
  [getRules, getMasterRules],
  (ruleByIdLookup, masterRuleByIdLookup) => {
    return Object.entries(ruleByIdLookup).reduce((acc, [ruleId, rule]) => {
      if (rule.master_rule_id) {
        const masterRule = masterRuleByIdLookup[rule.master_rule_id];
        if (masterRule) {
          acc[ruleId] = RuleCustomisableFields.reduce((acc, field) => {
            if (masterRule[field] !== rule[field]) {
              acc[field] = masterRule[field];
            }
            return acc;
          }, {});
        }
      }
      return acc;
    }, {});
  },
);

export const selectRulesAggridDataByRuleBookIdLookup = createSelector(
  [
    selectRuleIdsByRuleBookIdLookup,
    getRules,
    getMasterRules,
    getMasterRuleBooks,
    getBillingTags,
    selectMappedLedgerAccountByCodeLookup,
    selectParsedRuleContentByRuleIdLookup,
    selectCustomisationsByRuleIdLookup,
    selectPrerequisiteRuleChainByRuleIdLookup,
    getRuleSets,
    getMasterRuleSets,
  ],
  (
    ruleIdsByRuleBookIdLookup,
    ruleLookup,
    masterRuleLookup,
    masterRuleBookLookup,
    billingTagLookup,
    mappedLedgerAccountByCodeLookup,
    parsedRuleContentByRuleIdLookup,
    customisationsByRuleIdLookup,
    ruleChainByRuleIdLookup,
    ruleSetByIdLookup,
    masterRuleSetByIdLookup,
  ) => {
    return Object.entries(ruleIdsByRuleBookIdLookup).reduce(
      (acc, [ruleBookId, ruleIds]) => {
        acc[ruleBookId] = ruleIds.map(ruleId => {
          const rule = ruleLookup[ruleId];
          const masterRule =
            masterRuleLookup[ruleLookup[ruleId]?.master_rule_id];
          const parsedMasterRule = masterRule
            ? Object.assign({}, masterRule, {
                parsedContent: JSON.parse(masterRule.content) || {},
              })
            : masterRule;
          const parsedContent = parsedRuleContentByRuleIdLookup[ruleId];
          parsedContent.gst_status = getGSTStatusFromRule(parsedContent);
          parsedContent.billing_tags = parsedContent.billing_tags?.map(
            billingTagId => billingTagLookup[billingTagId]?.name,
          );

          const ruleSet = ruleSetByIdLookup[rule.rule_set_id];
          const masterRuleSet =
            masterRuleSetByIdLookup[masterRule?.rule_set_id] || null;

          const masterRuleStatus = getMasterRuleStatus(
            rule,
            masterRule,
            ruleSet,
            masterRuleSet,
          );
          const parsedRule = Object.assign({}, rule, { parsedContent });
          return {
            isCustomised:
              !isEmpty(customisationsByRuleIdLookup[rule.id]) || null, // return true if customised or null
            rule: parsedRule,
            basedOn:
              ruleChainByRuleIdLookup[rule.id]?.inputPathParts.filter(
                Boolean,
              ) || null,
            masterRule: parsedMasterRule,
            masterRuleStatus,
            masterRuleBook: masterRuleBookLookup[masterRule?.rule_book_id],
            ledgerAccount:
              rule.gl_code?.source === ValueSource.CONSTANT
                ? mappedLedgerAccountByCodeLookup[rule.gl_code.value]
                : null,
            ruleSet,
          };
        });
        return acc;
      },
      {},
    );
  },
);

export const selectRulesAggridDataByRuleSetIdLookup = createSelector(
  [getRuleSets, selectRulesAggridDataByRuleBookIdLookup],
  (ruleSetsLookup, rulesAggridDataByRuleBookIdLookup) => {
    return Object.keys(ruleSetsLookup).reduce((acc, ruleSetId) => {
      acc[ruleSetId] = flatten(
        Object.values(rulesAggridDataByRuleBookIdLookup),
      ).filter(data => data.ruleSet?.id === ruleSetId);
      return acc;
    }, {});
  },
);
